import {
  PartnerConfig,
  PartnerUser,
  fetchPartnerConfig,
  getPartnerUserFromUrl,
  savePartnerUser,
  getStoredPartnerUser,
  cleanPartnerParamsFromUrl,
} from './partners';

// We don't need to redeclare HeyGoodGame as it's already declared in vanilla/index.ts
// Just declare the additional window properties
declare global {
  interface Window {
    eh2: string;
    ehttd: string;
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleOneTapConfig) => void;
          prompt: (
            callback?: (notification: GooglePromptNotification) => void,
          ) => void;
        };
      };
    };
  }
}

interface GoogleOneTapConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface GooglePromptNotification {
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
}

const AUTH_CHECK_STORAGE_KEY = 'heygg_last_auth_check';
const AUTH_CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
const DEFAULT_API_BASE_URL = 'https://play.hey.gg';

function getQueryParam(param: string) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(param);
}

function setLocalStorage(key: string, value: number | string | null) {
  if (value === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(
      key,
      typeof value !== 'string' ? JSON.stringify(value) : value,
    );
  }
}

function getLocalStorage(key: string) {
  const value = localStorage.getItem(key);
  return value ? (typeof value === 'string' ? value : JSON.parse(value)) : null;
}

function redirectToCheckAuth(apiBaseUrl: string, anonymousId?: string | null, partnerUser?: PartnerUser | null) {
  const redirectParams = new URLSearchParams();
  redirectParams.set('redirect_uri', window.location.href);
  if (anonymousId) {
    redirectParams.set('anonymous_id', anonymousId);
  }
  if (partnerUser) {
    redirectParams.set(partnerUser.param_name, partnerUser.partner_user_id);
  }
  window.location.href = `${apiBaseUrl}/check-auth?${redirectParams.toString()}`;
}

/**
 * Checks and manages user authentication state for the HeyGoodGame platform.
 *
 * This function orchestrates the complete authentication flow, handling both
 * authenticated users with JWT tokens and anonymous users with session IDs.
 * It processes URL parameters, validates existing tokens, and manages periodic
 * re-authentication to ensure users maintain proper access to the platform.
 *
 * @example
 * ```typescript
 * // Called automatically by the auth module
 * await checkAuth();
 * ```
 *
 * @remarks
 * **Authentication Flow Overview:**
 *
 * The function follows a sequential flow to determine user authentication status:
 *
 * **Phase 1: URL Parameter Processing**
 * - Extracts `token` and `anonymous_id` from URL query parameters
 * - Immediately cleans the URL by removing these sensitive parameters from browser history
 * - This prevents tokens from being exposed in browser history or shared URLs
 *
 * **Phase 2: Initial Token Handling**
 * - **If `token` parameter exists:**
 *   - Stores JWT token in localStorage (`heygg_jwt`)
 *   - Makes API call to `/api/user` to fetch user profile data
 *   - Sets global email hash variables (`window.eh2`, `window.ehttd`) for tracking
 *   - Stores user data in localStorage (`heygg_user`)
 *   - Clears any existing anonymous ID since user is now authenticated
 *   - Updates last auth check timestamp and exits
 *
 * - **If `anonymous_id` parameter exists:**
 *   - Stores anonymous ID in localStorage (`heygg_anonymous_id`)
 *   - Clears any existing JWT token and user data (anonymous session)
 *   - Clears global email hash variables
 *   - Updates last auth check timestamp and exits
 *
 * **Phase 3: Existing Authentication Validation**
 * - **If neither JWT nor anonymous ID exists:**
 *   - Immediately redirects to `/check-auth` endpoint for fresh authentication
 *
 * - **If existing JWT token is found:**
 *   - Validates token by calling `/api/user` endpoint with Authorization header
 *   - **On successful validation:**
 *     - Updates global email hash variables with fresh user data
 *     - Refreshes user data in localStorage
 *     - Clears any conflicting anonymous ID
 *     - Updates auth check timestamp and exits
 *   - **On validation failure:**
 *     - Clears invalid JWT token and user data from localStorage
 *     - Preserves existing anonymous ID if present (graceful degradation)
 *     - Clears global email hash variables
 *     - Continues to next phase
 *
 * **Phase 4: Anonymous Session & Periodic Re-authentication**
 * - Checks if 24 hours have elapsed since last authentication check
 * - **If anonymous ID exists AND within 24-hour window:**
 *   - Continues with existing anonymous session (no redirect)
 * - **If 24-hour interval has been exceeded OR no anonymous ID:**
 *   - Redirects to `/check-auth` endpoint for fresh authentication
 *   - Passes existing anonymous ID as parameter to preserve session continuity
 *
 * **Phase 5: Redirect Behavior**
 * - All redirects go to `${apiBaseUrl}/check-auth` endpoint
 * - Current page URL is passed as `redirect_uri` parameter for post-auth return
 * - Existing `anonymous_id` is preserved in redirect URL when available
 * - This ensures users return to their intended destination after authentication
 *
 * **LocalStorage Management:**
 * - `heygg_jwt`: JWT token for authenticated users (cleared on logout/invalid token)
 * - `heygg_user`: Complete user profile data object (cleared on logout/invalid token)
 * - `heygg_anonymous_id`: Anonymous session identifier (persists across auth failures)
 * - `heygg_last_auth_check`: Timestamp of last successful auth check (24h interval)
 *
 * **Global State Side Effects:**
 * - `window.eh2`: Email hash for authenticated users (cleared for anonymous)
 * - `window.ehttd`: Email hash variant for authenticated users (cleared for anonymous)
 * - These global variables enable client-side tracking and personalization
 *
 * **Error Handling:**
 * - Network failures during token validation are logged but don't block execution
 * - Invalid tokens are gracefully cleared while preserving anonymous sessions
 * - API errors during user data fetching are caught and logged
 * - All errors allow fallback to anonymous session or re-authentication flow
 */
async function checkAuth() {
  console.log('[HEYGG COMMON] Checking auth...');
  const API_BASE_URL =
    window.HeyGoodGame?.config?.apiBaseUrl || DEFAULT_API_BASE_URL;
  const enablePartners = window.HeyGoodGame?.config?.enablePartners || false;
  const now = Date.now();
  const initialToken = getQueryParam('token');
  const initialAnonymousId = getQueryParam('anonymous_id');
  
  // Handle partner user IDs if enabled
  let partnerUser: PartnerUser | null = null;
  let partnerConfigs: PartnerConfig[] = [];
  
  if (enablePartners) {
    partnerConfigs = await fetchPartnerConfig(API_BASE_URL);
    partnerUser = getPartnerUserFromUrl(partnerConfigs);
    
    if (partnerUser) {
      console.log('[HEYGG COMMON] Found partner user:', partnerUser);
      savePartnerUser(partnerUser);
    }
  }

  if (initialToken || initialAnonymousId || partnerUser) {
    const cleanSearchParams = new URLSearchParams(window.location.search);
    cleanSearchParams.delete('token');
    cleanSearchParams.delete('anonymous_id');
    
    // Clean partner params if partners are enabled
    if (enablePartners) {
      const cleanedParams = cleanPartnerParamsFromUrl(partnerConfigs, partnerUser);
      // Copy over the cleaned params
      const newSearchParams = new URLSearchParams();
      cleanedParams.forEach((value, key) => {
        if (key !== 'token' && key !== 'anonymous_id') {
          newSearchParams.set(key, value);
        }
      });
      const newUrl = `${window.location.pathname}${
        newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''
      }${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    } else {
      const newUrl = `${window.location.pathname}${
        cleanSearchParams.toString() ? `?${cleanSearchParams.toString()}` : ''
      }${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
  }

  if (initialToken) {
    setLocalStorage('heygg_jwt', initialToken);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        headers: {
          Authorization: `Bearer ${initialToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch user details');
      const userData = await response.json();
      window.eh2 = userData.email_eh2;
      window.ehttd = userData.email_ehttd;
      setLocalStorage('heygg_user', userData);
      setLocalStorage('heygg_anonymous_id', null);
    } catch (err: any) {
      console.error(err.message || 'Failed to fetch user data');
    }
    setLocalStorage(AUTH_CHECK_STORAGE_KEY, now);
    return;
  }

  if (initialAnonymousId) {
    setLocalStorage('heygg_anonymous_id', initialAnonymousId);
    setLocalStorage('heygg_jwt', null);
    setLocalStorage('heygg_user', null);
    window.eh2 = '';
    window.ehttd = '';
    setLocalStorage(AUTH_CHECK_STORAGE_KEY, now);
    return;
  }

  const existingJwt = getLocalStorage('heygg_jwt');
  const existingAnonymousId = getLocalStorage('heygg_anonymous_id');

  if (!existingJwt && !existingAnonymousId) {
    const storedPartnerUser = enablePartners ? getStoredPartnerUser() : null;
    redirectToCheckAuth(API_BASE_URL, existingAnonymousId, storedPartnerUser);
    return;
  }

  if (existingJwt) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        headers: {
          Authorization: `Bearer ${existingJwt}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const userData = await response.json();
        window.eh2 = userData.email_eh2;
        window.ehttd = userData.email_ehttd;
        setLocalStorage('heygg_user', userData);
        setLocalStorage('heygg_anonymous_id', null);
        setLocalStorage(AUTH_CHECK_STORAGE_KEY, now);
        return;
      }
      // JWT is invalid, clear it but keep anonymous ID if it exists
      setLocalStorage('heygg_jwt', null);
      setLocalStorage('heygg_user', null);
      window.eh2 = '';
      window.ehttd = '';
    } catch (err) {
      console.warn('Failed to validate existing token:', err);
      // On error, clear JWT but keep anonymous ID
      setLocalStorage('heygg_jwt', null);
      setLocalStorage('heygg_user', null);
      window.eh2 = '';
      window.ehttd = '';
    }
  }

  const lastAuthCheck = getLocalStorage(AUTH_CHECK_STORAGE_KEY) || 0;
  const shouldForceCheck = now - lastAuthCheck > AUTH_CHECK_INTERVAL;
  console.log('[HEYGG COMMON] Auth check interval:', AUTH_CHECK_INTERVAL, 'ms');
  console.log('[HEYGG COMMON] Last auth check:', lastAuthCheck, 'ms');
  console.log('[HEYGG COMMON] Current time:', now, 'ms');
  console.log('[HEYGG COMMON] Should force check:', shouldForceCheck);

  // If we have an anonymous ID and we're not due for a forced check, use it
  if (existingAnonymousId && !shouldForceCheck) {
    console.log('[HEYGG COMMON] Using existing anonymous ID:', existingAnonymousId);
    return;
  }

  console.log('[HEYGG COMMON] No valid auth - redirecting to check-auth');
  const storedPartnerUser = enablePartners ? getStoredPartnerUser() : null;
  redirectToCheckAuth(API_BASE_URL, existingAnonymousId, storedPartnerUser);
}

const Google = {
  async handleGoogleCredential(
    credential: string,
    redirectUri?: string,
    apiBaseUrl?: string,
  ) {
    console.log('[HEYGG COMMON] Handling Google credential:', credential);
    const API_BASE_URL =
      apiBaseUrl || window.HeyGoodGame?.config?.apiBaseUrl || DEFAULT_API_BASE_URL;
    const existingAnonymousId = getLocalStorage('heygg_anonymous_id');

    try {
      const body: any = { credential };
      if (redirectUri) body.redirect_uri = redirectUri;
      if (existingAnonymousId) body.anonymous_id = existingAnonymousId;
      
      // Include partner user if available
      const enablePartners = window.HeyGoodGame?.config?.enablePartners || false;
      if (enablePartners) {
        const storedPartnerUser = getStoredPartnerUser();
        if (storedPartnerUser) {
          body[storedPartnerUser.param_name] = storedPartnerUser.partner_user_id;
        }
      }

      const response = await fetch(`${API_BASE_URL}/auth/google/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to verify Google credential');
      }

      const data = await response.json();

      console.log('[HEYGG COMMON] Google auth response:', data);

      if (data.success && data.token) {
        let returnUrl;

        if (data.is_new_user) {
          console.log('[HEYGG COMMON] New user detected');
          // New user - redirect to welcome page after establishing session
          returnUrl = encodeURIComponent(
            `${API_BASE_URL}/welcome?return_to=${encodeURIComponent(redirectUri || '')}`,
          );
        } else {
          // Existing user - redirect back to intended destination
          returnUrl = encodeURIComponent(data.redirect_url);
        }
        console.log('[HEYGG COMMON] Redirecting to:', returnUrl);

        console.log(
          '[HEYGG COMMON] Establishing SSO session with token:',
          data.token,
        );
        const ssoUrl = `${API_BASE_URL}/auth/sso/establish-session?token=${data.token}&return_to=${returnUrl}`;
        window.location.href = ssoUrl;
      } else if (data.success && data.redirect_url) {
        // No token case (shouldn't happen for game sites, but fallback)
        window.location.href = data.redirect_url;
      } else {
        throw new Error('Invalid response from Google auth endpoint');
      }
    } catch (error) {
      console.error('Error handling Google credential:', error);
      throw error;
    }
  },

  initializeOneTap(
    clientId: string,
    options: {
      autoSelect?: boolean;
      cancelOnTapOutside?: boolean;
      apiBaseUrl?: string;
    } = {},
  ) {
    if (!window.google?.accounts?.id) {
      console.error('Google One Tap library not loaded');
      return;
    }

    const config: GoogleOneTapConfig = {
      client_id: clientId,
      callback: (response: GoogleCredentialResponse) => {
        this.handleGoogleCredential(
          response.credential,
          window.location.href,
          options.apiBaseUrl,
        ).catch((error) => {
          console.error('Google One Tap authentication failed:', error);
        });
      },
      auto_select: options.autoSelect ?? false,
      cancel_on_tap_outside: options.cancelOnTapOutside ?? true,
    };

    window.google.accounts.id.initialize(config);
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        console.log(
          'Google One Tap not displayed:',
          notification.getNotDisplayedReason(),
        );
      } else if (notification.isSkippedMoment()) {
        console.log('Google One Tap skipped:', notification.getSkippedReason());
      } else if (notification.isDismissedMoment()) {
        console.log('Google One Tap dismissed:', notification.getDismissedReason());
      }
    });
  },
};

export { Google };

export default function auth() {
  return checkAuth();
}
